export const SOCIAL_PROVIDER_KEYS = [
	"bluesky",
	"facebook",
	"instagram",
	"linkedin",
	"pinterest",
	"tiktok",
	"threads",
	"x",
	"youtube",
] as const;

export type SocialProviderKey = (typeof SOCIAL_PROVIDER_KEYS)[number];
export type SocialContentFormat = "text" | "image" | "video";

export type ProviderPublishedEditSupport = {
	media: boolean;
	text: boolean;
};

export type ProviderMediaConstraints = {
	allowedAspectRatios?: readonly string[];
	altText?: {
		maxLength?: number;
		supported: boolean;
	};
	maxDurationSeconds?: number;
	maxFileSizeBytes?: number;
	maxImages?: number;
	maxVideos?: number;
	minDurationSeconds?: number;
	requiresBoard?: boolean;
	requiresTitle?: boolean;
	requiredFields?: readonly string[];
};

export type SocialProviderDefinition = {
	captionLimit: number;
	key: SocialProviderKey;
	label: string;
	mediaConstraints: Partial<Record<SocialContentFormat, ProviderMediaConstraints>>;
	publishedEditSupport: ProviderPublishedEditSupport;
	supports: readonly SocialContentFormat[];
};

export const SOCIAL_PROVIDER_CATALOG = {
	bluesky: {
		captionLimit: 300,
		key: "bluesky",
		label: "Bluesky",
		mediaConstraints: {
			image: {
				altText: { maxLength: 1000, supported: true },
				maxFileSizeBytes: 1_000_000,
				maxImages: 4,
			},
			video: {
				maxDurationSeconds: 60,
				maxFileSizeBytes: 50_000_000,
				maxVideos: 1,
			},
		},
		publishedEditSupport: { media: false, text: true },
		supports: ["text", "image", "video"],
	},
	facebook: {
		captionLimit: 63206,
		key: "facebook",
		label: "Facebook",
		mediaConstraints: {
			image: {
				altText: { supported: false },
				maxFileSizeBytes: 10_000_000,
				maxImages: 10,
			},
			video: {
				maxDurationSeconds: 14_400,
				maxFileSizeBytes: 10_000_000_000,
				maxVideos: 1,
			},
		},
		publishedEditSupport: { media: false, text: false },
		supports: ["text", "image", "video"],
	},
	instagram: {
		captionLimit: 2200,
		key: "instagram",
		label: "Instagram",
		mediaConstraints: {
			image: {
				allowedAspectRatios: ["4:5", "1:1", "1.91:1"],
				altText: { maxLength: 1000, supported: true },
				maxFileSizeBytes: 8_000_000,
				maxImages: 10,
				requiredFields: ["asset"],
			},
			video: {
				allowedAspectRatios: ["9:16", "4:5", "1:1", "1.91:1"],
				altText: { maxLength: 1000, supported: true },
				maxDurationSeconds: 900,
				maxFileSizeBytes: 1_000_000_000,
				maxVideos: 1,
				minDurationSeconds: 3,
				requiredFields: ["asset"],
			},
		},
		publishedEditSupport: { media: false, text: false },
		supports: ["image", "video"],
	},
	linkedin: {
		captionLimit: 3000,
		key: "linkedin",
		label: "LinkedIn",
		mediaConstraints: {
			image: {
				altText: { maxLength: 4086, supported: true },
				maxFileSizeBytes: 10_000_000,
				maxImages: 9,
			},
			video: {
				maxDurationSeconds: 600,
				maxFileSizeBytes: 5_000_000_000,
				maxVideos: 1,
				minDurationSeconds: 3,
			},
		},
		publishedEditSupport: { media: false, text: true },
		supports: ["text", "image", "video"],
	},
	pinterest: {
		captionLimit: 500,
		key: "pinterest",
		label: "Pinterest",
		mediaConstraints: {
			image: {
				allowedAspectRatios: ["2:3", "1:1", "9:16"],
				altText: { maxLength: 500, supported: true },
				maxFileSizeBytes: 20_000_000,
				maxImages: 1,
				requiresBoard: true,
				requiredFields: ["asset", "boardId"],
			},
			video: {
				allowedAspectRatios: ["2:3", "9:16", "1:1"],
				maxDurationSeconds: 900,
				maxFileSizeBytes: 2_000_000_000,
				maxVideos: 1,
				minDurationSeconds: 4,
				requiresBoard: true,
				requiredFields: ["asset", "boardId"],
			},
		},
		publishedEditSupport: { media: false, text: true },
		supports: ["image", "video"],
	},
	threads: {
		captionLimit: 500,
		key: "threads",
		label: "Threads",
		mediaConstraints: {
			image: {
				altText: { maxLength: 1000, supported: true },
				maxFileSizeBytes: 8_000_000,
				maxImages: 10,
			},
			video: {
				maxDurationSeconds: 300,
				maxFileSizeBytes: 1_000_000_000,
				maxVideos: 1,
			},
		},
		publishedEditSupport: { media: false, text: false },
		supports: ["text", "image", "video"],
	},
	tiktok: {
		captionLimit: 2200,
		key: "tiktok",
		label: "TikTok",
		mediaConstraints: {
			image: {
				maxFileSizeBytes: 20_000_000,
				maxImages: 35,
				requiredFields: ["asset"],
			},
			video: {
				allowedAspectRatios: ["9:16", "1:1", "16:9"],
				maxDurationSeconds: 600,
				maxFileSizeBytes: 4_000_000_000,
				maxVideos: 1,
				minDurationSeconds: 3,
				requiredFields: ["asset"],
			},
		},
		publishedEditSupport: { media: false, text: false },
		supports: ["image", "video"],
	},
	x: {
		captionLimit: 280,
		key: "x",
		label: "X",
		mediaConstraints: {
			image: {
				altText: { maxLength: 1000, supported: true },
				maxFileSizeBytes: 5_000_000,
				maxImages: 4,
			},
			video: {
				maxDurationSeconds: 140,
				maxFileSizeBytes: 512_000_000,
				maxVideos: 1,
			},
		},
		publishedEditSupport: { media: false, text: true },
		supports: ["text", "image", "video"],
	},
	youtube: {
		captionLimit: 5000,
		key: "youtube",
		label: "YouTube",
		mediaConstraints: {
			video: {
				allowedAspectRatios: ["9:16"],
				maxDurationSeconds: 180,
				maxFileSizeBytes: 256_000_000_000,
				maxVideos: 1,
				minDurationSeconds: 1,
				requiresTitle: true,
				requiredFields: ["asset", "title"],
			},
		},
		publishedEditSupport: { media: false, text: false },
		supports: ["video"],
	},
} as const satisfies Record<SocialProviderKey, SocialProviderDefinition>;

function mapProviderCatalog<Value>(
	select: (provider: SocialProviderKey) => Value,
) {
	return Object.fromEntries(
		SOCIAL_PROVIDER_KEYS.map((provider) => [provider, select(provider)]),
	) as unknown as Record<SocialProviderKey, Value>;
}

export const PLATFORM_LABELS = mapProviderCatalog(
	(provider) => SOCIAL_PROVIDER_CATALOG[provider].label,
);

export const PROVIDER_SUPPORTS = mapProviderCatalog(
	(provider) =>
		SOCIAL_PROVIDER_CATALOG[provider]
			.supports as readonly SocialContentFormat[],
);

export const PROVIDER_PUBLISHED_EDIT_SUPPORT = mapProviderCatalog(
	(provider) => SOCIAL_PROVIDER_CATALOG[provider].publishedEditSupport,
);

export const PROVIDER_CAPTION_LIMITS = mapProviderCatalog(
	(provider) => SOCIAL_PROVIDER_CATALOG[provider].captionLimit,
);

export const PROVIDER_MEDIA_CONSTRAINTS: Record<
	SocialProviderKey,
	Partial<Record<SocialContentFormat, ProviderMediaConstraints>>
> = mapProviderCatalog(
	(provider) => SOCIAL_PROVIDER_CATALOG[provider].mediaConstraints,
);

export const CONNECT_PROVIDER_ORDER = [
	"instagram",
	"facebook",
	"linkedin",
	"x",
	"tiktok",
	"youtube",
	"pinterest",
	"threads",
	"bluesky",
] as const satisfies readonly SocialProviderKey[];

export const COMPOSE_PROVIDER_ORDER = [
	"facebook",
	"bluesky",
	"linkedin",
	"threads",
	"x",
	"instagram",
	"pinterest",
	"tiktok",
	"youtube",
] as const satisfies readonly SocialProviderKey[];

const SOCIAL_PROVIDER_SET = new Set<string>(SOCIAL_PROVIDER_KEYS);

export function isSocialProviderKey(value: string): value is SocialProviderKey {
	return SOCIAL_PROVIDER_SET.has(value);
}

export function formatSocialProviderLabel(provider: string) {
	return isSocialProviderKey(provider)
		? PLATFORM_LABELS[provider]
		: provider.charAt(0).toUpperCase() + provider.slice(1);
}
